package fr.manyverse.app.presentation.components.player_screen.inventory

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Send
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material3.ElevatedCard
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import fr.manyverse.app.domain.model.InventoryItem
import fr.manyverse.app.domain.model.ItemCategory
import fr.manyverse.app.presentation.components.ZoomableImageDialog
import fr.manyverse.app.presentation.components.inventory.InventoryItemImage
import fr.manyverse.app.presentation.viewmodel.InventoryViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.inventory_delete_item_cd
import manyverse.shared.generated.resources.inventory_give_item_cd
import org.jetbrains.compose.resources.stringResource

@Composable
fun InventoryItemCard(
    item: InventoryItem,
    displayQuantity: Int,
    viewModel: InventoryViewModel,
    onDeleteClick: () -> Unit,
    onGiveClick: () -> Unit,
    showGiveButton: Boolean = true,
    modifier: Modifier = Modifier
) {
    var signedImageUrl by remember(item.imageUrl) { mutableStateOf<String?>(null) }
    var showFullscreenImage by remember { mutableStateOf(false) }

    LaunchedEffect(item.imageUrl) {
        if (item.imageUrl != null) {
            viewModel.getSignedImageUrl(item.imageUrl).onSuccess { url ->
                signedImageUrl = url
            }
        }
    }

    ElevatedCard(modifier = modifier.padding(4.dp)) {
        Column {
            InventoryItemImage(
                signedImageUrl = signedImageUrl,
                itemName = item.name,
                category = item.category,
                onClick = { if (signedImageUrl != null) showFullscreenImage = true },
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(1f)
            )

            Text(
                text = item.name,
                style = MaterialTheme.typography.bodySmall,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.padding(horizontal = 6.dp, vertical = 4.dp)
            )

            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                if (item.category == ItemCategory.CONSUMABLE) {
                    CompactQuantityControls(
                        quantity = displayQuantity,
                        onDecrement = { viewModel.decrementQuantity(item) },
                        onIncrement = { viewModel.incrementQuantity(item) }
                    )
                    if (showGiveButton) {
                        IconButton(onClick = onGiveClick, modifier = Modifier.size(36.dp)) {
                            Icon(
                                imageVector = Icons.AutoMirrored.Filled.Send,
                                contentDescription = stringResource(Res.string.inventory_give_item_cd, item.name),
                                tint = MaterialTheme.colorScheme.primary,
                                modifier = Modifier.size(16.dp)
                            )
                        }
                    }
                    IconButton(onClick = onDeleteClick, modifier = Modifier.size(36.dp)) {
                        Icon(
                            imageVector = Icons.Default.Delete,
                            contentDescription = stringResource(Res.string.inventory_delete_item_cd, item.name),
                            tint = MaterialTheme.colorScheme.error,
                            modifier = Modifier.size(16.dp)
                        )
                    }
                } else {
                    if (showGiveButton) {
                        IconButton(onClick = onGiveClick, modifier = Modifier.size(36.dp)) {
                            Icon(
                                imageVector = Icons.AutoMirrored.Filled.Send,
                                contentDescription = stringResource(Res.string.inventory_give_item_cd, item.name),
                                tint = MaterialTheme.colorScheme.primary,
                                modifier = Modifier.size(16.dp)
                            )
                        }
                    }
                    IconButton(onClick = onDeleteClick, modifier = Modifier.size(36.dp)) {
                        Icon(
                            imageVector = Icons.Default.Delete,
                            contentDescription = stringResource(Res.string.inventory_delete_item_cd, item.name),
                            tint = MaterialTheme.colorScheme.error,
                            modifier = Modifier.size(16.dp)
                        )
                    }
                }
            }
        }
    }

    if (showFullscreenImage && signedImageUrl != null) {
        ZoomableImageDialog(
            imageUrl = signedImageUrl!!,
            contentDescription = item.name,
            onDismiss = { showFullscreenImage = false }
        )
    }
}

@Composable
private fun CompactQuantityControls(
    quantity: Int,
    onDecrement: () -> Unit,
    onIncrement: () -> Unit
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(0.dp)
    ) {
        IconButton(
            onClick = onDecrement,
            enabled = quantity > 1,
            modifier = Modifier.size(28.dp)
        ) {
            Text("−", style = MaterialTheme.typography.titleMedium)
        }
        Text(
            text = quantity.toString(),
            style = MaterialTheme.typography.titleMedium,
            modifier = Modifier.padding(horizontal = 2.dp)
        )
        IconButton(
            onClick = onIncrement,
            enabled = quantity < 999,
            modifier = Modifier.size(28.dp)
        ) {
            Text("+", style = MaterialTheme.typography.titleMedium)
        }
    }
}

